/**
 * Chat Members Panel
 * Displays and manages chat members with role-based permissions
 */

import React, { useState } from 'react';
import {
  List,
  Avatar,
  Button,
  Typography,
  Tag,
  Popconfirm,
  Select,
  message,
  Empty,
  Spin,
} from 'antd';
import {
  UserOutlined,
  CrownOutlined,
  SafetyOutlined,
  UserAddOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  useChatMembers,
  useAddMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from '@/hooks/useChat';
import { useChatMemberEvents } from '@/hooks/useChatMemberEvents';
import { useUsers } from '@/hooks/useTenants';
import { ChatMemberRole } from '@/api/types/chat.types';
import { useAuth } from '@/context/AuthContext';
import './ChatMembersPanel.css';

const { Text } = Typography;

interface ChatMembersPanelProps {
  chatId: string;
  myRole: ChatMemberRole | null;
}

export const ChatMembersPanel: React.FC<ChatMembersPanelProps> = ({
  chatId,
  myRole,
}) => {
  const { user } = useAuth();
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Queries
  const { data: members, isLoading: loadingMembers } = useChatMembers(chatId);
  const { data: tenantUsers, isLoading: loadingUsers } = useUsers();

  // Mutations
  const { mutate: addMembers, isPending: addingMembers } = useAddMembers();
  const { mutate: removeMember, isPending: removingMember } = useRemoveMember();
  const { mutate: updateRole, isPending: updatingRole } = useUpdateMemberRole();

  // WebSocket events - listen for member changes in real-time
  useChatMemberEvents({
    enabled: true,
    showNotifications: true,
  });

  // Permissions
  const canManageMembers =
    myRole === ChatMemberRole.ADMIN || myRole === ChatMemberRole.OWNER;
  const canChangeRoles = myRole === ChatMemberRole.OWNER;

  // Get users that are not already members
  const availableUsers = tenantUsers?.data?.filter(
    (user) => !members?.some((member) => member.user_id === user.__meta__.id)
  );

  const handleAddMembers = () => {
    if (selectedUserIds.length === 0) {
      message.warning('Please select at least one member to add');
      return;
    }

    addMembers(
      { chatId, userIds: selectedUserIds },
      {
        onSuccess: () => {
          message.success(
            `Added ${selectedUserIds.length} member${selectedUserIds.length > 1 ? 's' : ''}`
          );
          setSelectedUserIds([]);
          setShowAddMembers(false);
        },
        onError: (error) => {
          console.error('Failed to add members:', error);
          message.error('Failed to add members. Please try again.');
        },
      }
    );
  };

  const handleRemoveMember = (userId: string, displayName: string) => {
    removeMember(
      { chatId, userId },
      {
        onSuccess: () => {
          message.success(`Removed ${displayName} from chat`);
        },
        onError: (error) => {
          console.error('Failed to remove member:', error);
          message.error('Failed to remove member. Please try again.');
        },
      }
    );
  };

  const handleUpdateRole = (
    userId: string,
    newRole: ChatMemberRole.ADMIN | ChatMemberRole.MEMBER,
    displayName: string
  ) => {
    updateRole(
      { chatId, userId, role: newRole },
      {
        onSuccess: () => {
          const roleLabel =
            newRole === ChatMemberRole.ADMIN ? 'admin' : 'member';
          message.success(`Changed ${displayName}'s role to ${roleLabel}`);
        },
        onError: (error) => {
          console.error('Failed to update role:', error);
          message.error('Failed to update role. Please try again.');
        },
      }
    );
  };

  const getRoleBadge = (role: ChatMemberRole) => {
    switch (role) {
      case ChatMemberRole.OWNER:
        return (
          <Tag icon={<CrownOutlined />} color="gold">
            Owner
          </Tag>
        );
      case ChatMemberRole.ADMIN:
        return (
          <Tag icon={<SafetyOutlined />} color="blue">
            Admin
          </Tag>
        );
      case ChatMemberRole.MEMBER:
        return (
          <Tag icon={<UserOutlined />} color="default">
            Member
          </Tag>
        );
    }
  };

  if (loadingMembers) {
    return (
      <div className="chat-members-panel">
        <Spin tip="Loading members..." />
      </div>
    );
  }

  return (
    <div className="chat-members-panel">
      <div className="chat-members-header">
        <Text strong>Members ({members?.length || 0})</Text>
        {canManageMembers && !showAddMembers && (
          <Button
            type="primary"
            size="small"
            icon={<UserAddOutlined />}
            onClick={() => setShowAddMembers(true)}
          >
            Add
          </Button>
        )}
      </div>

      {/* Add members section */}
      {showAddMembers && canManageMembers && (
        <div className="chat-members-add-section">
          <Select
            mode="multiple"
            placeholder="Select members to add"
            loading={loadingUsers}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            options={
              availableUsers?.map((user) => ({
                label: `${user.full_name || user.username}`,
                value: user.__meta__.id,
              })) || []
            }
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
            }
            style={{ width: '100%', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              size="small"
              onClick={handleAddMembers}
              loading={addingMembers}
              disabled={selectedUserIds.length === 0}
            >
              Add Members
            </Button>
            <Button
              size="small"
              onClick={() => {
                setShowAddMembers(false);
                setSelectedUserIds([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="chat-members-list">
        {!members || members.length === 0 ? (
          <Empty
            description="No members"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={members}
            renderItem={(member) => {
              const isMe = member.user_id === user?.id;
              const canRemove =
                canManageMembers &&
                !isMe &&
                member.role !== ChatMemberRole.OWNER;
              const canChangeRole =
                canChangeRoles && !isMe && member.role !== ChatMemberRole.OWNER;

              return (
                <List.Item className="chat-member-item">
                  <div className="chat-member-content">
                    <Avatar
                      icon={<UserOutlined />}
                      src={member.avatar_url}
                      style={{ backgroundColor: '#1890ff' }}
                    />
                    <div className="chat-member-info">
                      <Text strong>
                        {member.display_name}
                        {isMe && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            (You)
                          </Text>
                        )}
                      </Text>
                      <Text type="secondary" className="chat-member-email">
                        {member.email}
                      </Text>
                    </div>
                  </div>

                  <div className="chat-member-actions">
                    {getRoleBadge(member.role)}

                    {canChangeRole && member.role !== ChatMemberRole.OWNER && (
                      <Select
                        size="small"
                        value={member.role}
                        onChange={(newRole) =>
                          handleUpdateRole(
                            member.user_id,
                            newRole,
                            member.display_name
                          )
                        }
                        loading={updatingRole}
                        style={{ width: 100 }}
                        options={[
                          { label: 'Admin', value: ChatMemberRole.ADMIN },
                          { label: 'Member', value: ChatMemberRole.MEMBER },
                        ]}
                      />
                    )}

                    {canRemove && (
                      <Popconfirm
                        title="Remove member"
                        description={`Are you sure you want to remove ${member.display_name}?`}
                        onConfirm={() =>
                          handleRemoveMember(
                            member.user_id,
                            member.display_name
                          )
                        }
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={removingMember}
                        />
                      </Popconfirm>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
